/**
 * Rich integration picker for the attach-integration card. Built on
 * DropdownMenu for the popover/positioning/keyboard mechanics rather than a
 * native <select>, since each option needs a status dot and a two-line
 * provider/email label — theme/untitled/Select is intentionally a plain
 * <select> and isn't meant to grow that (see its own file comment).
 * Spec: assets/handoffs/design_handoff_attach_integration
 *
 * @param {Array} integrations - the full integrations collection; filtered
 *   here to available/connected ones only, per the handoff ("A select that
 *   shows only connected integrations")
 *   each item has {uuid, type, username, available}
 * @param {string} value - selected integration uuid
 * @param {function} onChange - (uuid) => void
 * @param {boolean=false} disabled
 * @param {string=} error
 */
import React from "react";
import classNames from "classnames";
import DropdownMenu from "../../theme/untitled/DropdownMenu/DropdownMenu";
import Icon from "../../theme/untitled/icons/Icon";
import { INTEGRATION_NAMES } from "../../../../consts";
import styles from "./IntegrationAttachSelect.module.scss";

const IntegrationAttachSelect = ({ integrations, value, onChange, disabled = false, error }) => {
  const connected = integrations.filter((item) => Number.parseInt(item.available, 10));
  const selected = connected.find((item) => item.uuid === value);
  const selectedLabel = selected
    ? `${INTEGRATION_NAMES[selected.type] || selected.type} · ${selected.username}`
    : null;

  const items = connected.map((item) => {
    const providerName = INTEGRATION_NAMES[item.type] || item.type;
    const isSelected = item.uuid === value;

    return {
      key: item.uuid,
      icon: <span className={styles.dot} />,
      label: (
        <span className={styles.option}>
          <span className={styles.optionText}>
            <span className={styles.optionProvider}>{providerName}</span>
            <span className={styles.optionEmail}>{item.username}</span>
          </span>
          {isSelected && <Icon name="Check" size={16} className={styles.optionCheck} />}
        </span>
      ),
      onClick: () => onChange(item.uuid),
    };
  });

  const trigger = (
    <button
      type="button"
      disabled={disabled}
      className={classNames(styles.trigger, { [styles["trigger--error"]]: error })}
    >
      <span
        className={classNames(styles.val, { [styles["val--placeholder"]]: !selected })}
        title={selectedLabel || undefined}
      >
        {selectedLabel || "Select an integration"}
      </span>
      <Icon name="ChevronDown" size={18} className={styles.chevron} />
    </button>
  );

  return (
    <div className={styles.root}>
      <DropdownMenu
        trigger={trigger}
        items={items}
        align="start"
        panelClassName={styles.panel}
      />
      {error && <p className={styles.error}>{error}</p>}
      <p className={styles.hint}>
        Only connected integrations are listed.{" "}
        <a href="/integrations" target="_blank" rel="noreferrer" className={styles.hintLink}>
          Open Integrations
          <Icon name="ArrowUpRight" size={14} />
        </a>
      </p>
    </div>
  );
};

export default IntegrationAttachSelect;
